/**
 * Between a session and the GM's asset library: reading what a version holds
 * to copy it in, and putting what was made at the table into the library.
 *
 * The table never READS the library to play (the user's ruling: copies, not
 * links) - these run only when the GM asks: copy in, update a copy, save a
 * palette, a map, a scene or a sheet at the table. Every failure throws a
 * sentence for the caller's toast.
 */
import { readFileVersion, readVersion, saveVersion, writeFolder, type Library } from './assetStore';
import { extensionFor } from './engine/assets/fileKinds';
import { KINDS, fileNameFor, type ContentOf } from './engine/assets/kinds';
import { storeThumb } from './assetThumbs';
import { newAsset, newFolderRow } from './engine/assets/rows';
import { uniqueName } from './engine/assets/tree';
import { fullSentence, wouldExceed, type BackendName } from './engine/assets/quota';
import { ROOT, type AssetKind, type AssetRow, type DocKind, type Note, type Origin } from './engine/assets/types';
import { ownedRow } from './engine/assets/provenance';
import type { ArmouryKind } from './engine/assets/armouryKinds';
import type { Terrain } from './engine/battlefield/presets';
import type { ParkedScene } from './engine/scenes/types';
import type { Character } from './engine/character/types';
import { mintId } from './utils/mintUnique';
import { castToHeal, healCast, originKey } from './engine/roster/healProtagonist';
import { castMayHaveLostProtagonists } from './engine/scenes/sceneFile';
import { diag, errorSummary } from './diagnostics/log';

export interface LibraryAccess {
  uid: string;
  lib: Library;
  backendName: BackendName;
}

/** Stored text decoded as its kind; throws the reader's sentence when it does not read. */
function decoded<K extends DocKind>(row: AssetRow, kind: K, text: string): ContentOf[K] {
  const { content, error } = KINDS[kind].decode(text);
  if (!content) throw new Error(`${row.name} could not be read: ${error}`);
  return content as ContentOf[K];
}

/** An asset's current version, decoded as its kind; throws the reader's sentence when it does not read. */
async function readCurrent<K extends DocKind>(access: LibraryAccess, row: AssetRow, kind: K): Promise<ContentOf[K]> {
  return decoded(row, kind, await readVersion(access.uid, row.id, row.version));
}

/** A map asset's current terrain. */
export async function readMapAsset(access: LibraryAccess, row: AssetRow): Promise<Terrain> {
  return (await readCurrent(access, row, 'map')).terrain;
}

/** A palette asset's surfaces and materials. */
export const readPaletteAsset = (access: LibraryAccess, row: AssetRow) => readCurrent(access, row, 'palette');

/**
 * A scene asset as it was saved - its state too - with fresh ids for the wings, under its card's name: a scene has
 * ONE name (2026-10-20), so a card renamed in the library comes in renamed, wherever it is read.
 */
export async function readSceneAsset(access: LibraryAccess, row: AssetRow): Promise<ParkedScene> {
  const text = await readVersion(access.uid, row.id, row.version);
  const parked = await healStoredScene(access, text, decoded(row, 'scene', text));
  return { ...parked, record: { ...parked.record, name: row.name } };
}

/** A character asset's sheet. */
export const readCharacterAsset = (access: LibraryAccess, row: AssetRow): Promise<Character> =>
  readCurrent(access, row, 'character');

/**
 * The library character a cast member was copied from, at THAT version - the rules he had then. A version since
 * pruned (the asset page's "delete older versions") falls back to the current one, the only rules left to give.
 * Throws when the character is gone from this library.
 */
async function readCopiedFrom(access: LibraryAccess, origin: Origin): Promise<Character> {
  const row = ownedRow(access.lib.assets, origin);
  if (!row) throw new Error(`library character ${origin.assetId} is gone`);
  try {
    return decoded(row, 'character', await readVersion(access.uid, row.id, origin.version));
  } catch (err) {
    diag.info('assets', `v${origin.version} of ${row.name} is gone - healing from v${row.version}`, errorSummary(err));
    return readCharacterAsset(access, row);
  }
}

/**
 * The scene as stored in `text`, with each cast member's Protagonist rules restored from the library character he
 * was copied from - only for a file written while the scene library stripped them (v1, `castMayHaveLostProtagonists`;
 * `engine/roster/healProtagonist`). A v2 cast without rules has none on purpose and is left alone. Reads in parallel;
 * a character that cannot be read heals nobody and is logged, never thrown - the scene still opens.
 */
async function healStoredScene(access: LibraryAccess, text: string, parked: ParkedScene): Promise<ParkedScene> {
  if (!castMayHaveLostProtagonists(JSON.parse(text))) return parked;
  const origins = castToHeal(parked.cast);
  if (!origins.length) return parked;
  const settled = await Promise.allSettled(origins.map((o) => readCopiedFrom(access, o)));
  const read = new Map<string, Character>();
  settled.forEach((result, i) => {
    if (result.status === 'fulfilled') read.set(originKey(origins[i]), result.value);
    else diag.warn('assets', 'a cast member’s library character could not be read', errorSummary(result.reason));
  });
  const cast = healCast(parked.cast, read);
  return cast === parked.cast ? parked : { ...parked, cast };
}

/** One of the Armoury's assets (plan armoury): an effect, a template, a weapon..., as its kind decodes it. */
export const readArmouryAsset = <K extends ArmouryKind>(access: LibraryAccess, row: AssetRow, kind: K) =>
  readCurrent(access, row, kind);

/** An image or handout asset's current file, named after the asset: what a handout is posted from. */
export async function readFileAsset(access: LibraryAccess, row: AssetRow): Promise<File> {
  const blob = await readFileVersion(access.uid, row.id, row.version);
  return new File([blob], fileNameFor(row.name, extensionFor(blob.type)), { type: blob.type });
}

/** The name of the folder things made at a table land in: "From session ABC123". */
const sessionFolderName = (label: string) => `From session ${label}`;

/** The folder things made at a table land in - "From session ABC123" - made once. */
async function sessionFolder(access: LibraryAccess, label: string): Promise<string> {
  const name = sessionFolderName(label);
  const found = access.lib.folders.find((f) => f.parentId === ROOT && f.name === name);
  if (found) return found.id;
  const folder = newFolderRow(mintId('f'), name, ROOT, Date.now());
  await writeFolder(access.uid, folder);
  return folder.id;
}

/** Save a version of `row` (or a new asset in the session's folder), checking the room first. */
async function saveToLibrary(access: LibraryAccess, row: AssetRow, text: string, note: Note): Promise<AssetRow> {
  if (wouldExceed(access.lib.assets, text.length, access.backendName, access.lib.foreignBytes))
    throw new Error(fullSentence(access.backendName));
  const saved = await saveVersion({ uid: access.uid, asset: row, text, note, now: Date.now() });
  void storeThumb(access.uid, saved, text);
  return saved;
}

/** A new asset made at the table in `folderId`, under a name unique there. */
async function newInFolder(access: LibraryAccess, folderId: string, made: Made, label: string): Promise<AssetRow> {
  const taken = access.lib.assets.filter((a) => a.folderId === folderId && !a.deletedAt).map((a) => a.name);
  const row = newAsset(mintId('a'), made.kind, uniqueName(taken, made.name), folderId, Date.now());
  return saveToLibrary(access, row, made.text, { kind: 'table', sessionName: label });
}

interface Made {
  kind: AssetKind;
  name: string;
  text: string;
}

/**
 * The library as one save left it, for the next save of the same batch: the view a batch is handed is a render
 * behind, and without this two saves make two session folders and give two new assets one name. The saved row in
 * place of its old one, and its folder when the save made it - only the session's folder is ever made at a table.
 */
export function afterSave(access: LibraryAccess, saved: AssetRow, label: string): LibraryAccess {
  const { assets, folders } = access.lib;
  const known = saved.folderId === ROOT || folders.some((f) => f.id === saved.folderId);
  const made = known ? [] : [newFolderRow(saved.folderId, sessionFolderName(label), ROOT, saved.updatedAt)];
  const lib = {
    ...access.lib,
    assets: [...assets.filter((a) => a.id !== saved.id), saved],
    folders: [...folders, ...made],
  };
  return { ...access, lib };
}

/** A new asset made at the table, in "From session <label>". */
export async function newFromTable(
  access: LibraryAccess,
  kind: AssetKind,
  wanted: string,
  text: string,
  label: string,
): Promise<AssetRow> {
  return newInFolder(access, await sessionFolder(access, label), { kind, name: wanted, text }, label);
}

/**
 * A new asset made at the table in the same folder as `beside`'s - the second Sicarian beside the first
 * (`engine/assets/saveBack`, 2026-11-04); in "From session <label>" when this library no longer holds that asset.
 */
export async function newBesideAtTable(
  access: LibraryAccess,
  made: Made,
  beside: Origin,
  label: string,
): Promise<AssetRow> {
  const row = ownedRow(access.lib.assets, beside);
  return newInFolder(access, row ? row.folderId : await sessionFolder(access, label), made, label);
}

/**
 * Something made or changed at the table, into the library: the next version of `origin`'s asset when this
 * library still owns it, else a new asset in "From session <label>" (a foreign or missing origin makes a new one).
 */
export async function saveMadeAtTable(
  access: LibraryAccess,
  made: { kind: AssetKind; name: string; text: string; origin?: Origin | null },
  label: string,
): Promise<AssetRow> {
  const row = ownedRow(access.lib.assets, made.origin);
  return row
    ? saveToLibrary(access, row, made.text, { kind: 'table', sessionName: label })
    : newFromTable(access, made.kind, made.name, made.text, label);
}
